import React, { useEffect, useState } from 'react'
import { Platform, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { useRoute, RouteProp } from '@react-navigation/native'
import Toast from 'react-native-toast-message'
import { Button, ScreenAuth, Text, TextInput } from '@components'
import { ScreenAuthProps } from '@navigation'
import { useLanguage } from '@context'
import { useResetPassword } from '@hooks'
import getStyle from './style'
import { useScreen } from '@hooks'
import { getCommonStyles, translateApiError } from '@utils'

type ResetPasswordRouteProp = RouteProp<
  { ResetPassword: { token?: string } },
  'ResetPassword'
>

export const ResetPassword: React.FC<ScreenAuthProps<'ResetPassword'>> = ({
  navigation,
}) => {
  const { t } = useLanguage()
  const theme = useTheme()
  const { isMobile } = useScreen()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const route = useRoute<ResetPasswordRouteProp>()

  const [token, setToken] = useState(route.params?.token ?? '')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')

  const { mutate: resetPasswordMutation, isPending } = useResetPassword()

  useEffect(() => {
    if (token) return
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      setToken(params.get('token') ?? '')
    }
  }, [token])

  const onBackButtonPress = () => {
    if(navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.reset({ index: 0, routes: [{ name: 'Login' }] })
    }
  }

  const handleResetPassword = () => {
    setError('')

    if (!token) {
      setError(t('resetPassword.error.noToken'))
      return
    }
    if (!newPassword || newPassword.length < 8) {
      setError(t('resetPassword.error.passwordTooShort'))
      return
    }
    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.error.passwordMismatch'))
      return
    }

    resetPasswordMutation(
      { token, newPassword },
      {
        onSuccess: () => {
          Toast.show({
            type: 'success',
            text1: t('resetPassword.success'),
          })
          navigation.navigate('Login')
        },
        onError: (err) => {
          const { message } = translateApiError(t, err)
          setError(message)
        },
      },
    )
  }

  return (
    <ScreenAuth
      title={t('resetPassword.title')}
      onPressBackIcon={onBackButtonPress}
      isPending={isPending}
    >
      <View style={[styles.container, !isMobile && commonStyles.flexMaxAuth]}>
        <TextInput
          placeholder={t('resetPassword.newPassword')}
          value={newPassword}
          onChangeText={setNewPassword}
          containerStyle={[styles.input, styles.paddingTop]}
          autoCapitalize={'none'}
          secureTextEntry
          iconRight="eye"
        />
        <TextInput
          placeholder={t('resetPassword.confirmPassword')}
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          containerStyle={styles.input}
          autoCapitalize={'none'}
          secureTextEntry
          iconRight="eye"
        />
        {error ? (
          <Text
            type="bodyExtraSmall"
            color={theme.colors.error}
            style={styles.errorText}
          >
            {error}
          </Text>
        ) : null}

        <View style={styles.buttonContainer}>
          <Button
            title={t('resetPassword.button')}
            onPress={handleResetPassword}
            disabled={isPending}
          />
        </View>
      </View>
    </ScreenAuth>
  )
}
